import { memo, useContext, useEffect, useMemo, useState } from 'react';
import CircularProgress from 'components/CircularProgress';
import { getSampledValuesListByTransactionId } from 'features/selectors';
import { loadSampledValuesByTransactionId } from 'features/Transactions';
import moment from 'moment-timezone';
import { UserSessionContext } from 'providers/UserSessionProvider';
import { useDispatch, useSelector } from 'react-redux';
import { Area, AreaChart, Label, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getSampledValuesByTransactionId } from 'services/sampledValues';
import roundToTwo from 'util/roundToTwo';

import './styles.scss';

import log from '@/lib/logger';
import { type Transaction } from '@/types/swagger';
import { AxiosError } from 'axios';

// TODO: Show SoC and Power from meterValues
// TODO: evitar que renderice tantas veces
const shouldNotUpdate = (prevProps: TransactionChartProps, nextProps: TransactionChartProps) => {
  if (
    prevProps.transaction &&
    nextProps.transaction &&
    prevProps.transaction.id !== nextProps.transaction.id
  ) {
    return false;
  }
  return true;
};

interface TransactionChartProps {
  transaction: Transaction;
}

const TransactionChart: React.FC<TransactionChartProps> = ({ transaction }) => {
  const dispatch = useDispatch();
  const { timezone } = useContext(UserSessionContext);
  const [isLoading, setIsLoading] = useState(false);
  const sampledValues = useSelector(getSampledValuesListByTransactionId);

  const powerValues = useMemo(
    () =>
      sampledValues?.[transaction?.id]
        ?.filter?.((sample) => sample.measurand === 'Power.Active.Import')
        .sort((a, b) => {
          const aMoment = moment(a.timestamp);
          const bMoment = moment(b.timestamp);
          if (aMoment > bMoment) return -1;
          if (aMoment < bMoment) return 1;
          return 0;
        }),
    [sampledValues, transaction]
  );

  const socValues = useMemo(
    () =>
      sampledValues?.[transaction?.id]
        ?.filter?.((sample) => sample.measurand === 'SoC')
        .sort((a, b) => {
          const aMoment = moment(a.timestamp);
          const bMoment = moment(b.timestamp);
          if (aMoment > bMoment) return -1;
          if (aMoment < bMoment) return 1;
          return 0;
        }),
    [sampledValues, transaction]
  );

  useEffect(() => {
    if (transaction) {
      setIsLoading(true);
      getSampledValuesByTransactionId(transaction.id)
        .then((data) => {
          if (data instanceof AxiosError) {
            log.error(data);
            return;
          }
          dispatch(loadSampledValuesByTransactionId(transaction.id, data, true));
          setIsLoading(false);
        })
        .catch((e) => {
          log.error(e);
          setIsLoading(false);
        });
    }
  }, [transaction, dispatch]);

  const data = useMemo(() => {
    const values =
      powerValues && powerValues.length > 0
        ? powerValues.map((pv) => ({ ...pv, value: parseFloat(pv.value) }))
        : socValues && socValues.length > 0
          ? socValues.map((sv) => ({ ...sv, value: parseFloat(sv.value) }))
          : undefined;

    if (!values) return undefined;

    return values
      .map((value) => {
        const power = powerValues.find((power) => power.timestamp === value.timestamp)?.value;
        return {
          Potencia: power ? roundToTwo(parseFloat(power) / 1000) : undefined,
          SoC: socValues.find((soc) => soc.timestamp === value.timestamp)?.value,
          Hora: value.timestamp,
        };
      })
      .reverse();
  }, [socValues, powerValues]);

  const maxValues: { maxPotencia: number; maxSoc: number } = useMemo(() => {
    if (!data) return { maxPotencia: 0, maxSoc: 0 };
    return data.reduce(
      (max, current) => {
        if (!current.SoC && current.Potencia)
          return {
            maxPotencia: Math.max(max.maxPotencia, parseFloat(current.Potencia)),
            maxSoc: max.maxSoc,
          };
        return {
          maxPotencia: Math.max(max.maxPotencia, parseFloat(current.Potencia || '0')),
          maxSoc: Math.max(max.maxSoc, parseFloat(current.SoC || '0')),
        };
      },
      { maxPotencia: -Infinity, maxSoc: -Infinity }
    );
  }, [data]);

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active: boolean;
    payload: { value: string; payload: { SoC: string; Potencia: string } }[];
    label: string;
  }) => {
    const socValue = payload.find(
      (payloadElement) =>
        payloadElement.payload.SoC?.toString() === payloadElement.value?.toString()
    );
    const powerValue = payload.find(
      (payloadElement) =>
        payloadElement.payload.Potencia?.toString() === payloadElement.value?.toString()
    );

    if (active) {
      return (
        <div className="custom-tooltip__container">
          <div className="custom-tooltip__label">
            {moment.utc(label).tz(timezone).format('MMM D - HH:mm:ss')}
          </div>
          {socValue ? (
            <div className="custom-tooltip__label">
              <div className="custom-tooltip__square bg-spring-green" />
              <span>{` SoC: ${socValue.value}`}</span>
            </div>
          ) : null}
          {powerValue ? (
            <div className="custom-tooltip__label">
              <div className="custom-tooltip__square bg-maya-blue" />
              <span>{` Potencia: ${powerValue.value}`}</span>
            </div>
          ) : null}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="transaction-chart-container">
      {isLoading && (
        <div className="loading-container m-5">
          <div className="loading-container__content">
            <CircularProgress />
            <p className="loading-container__text">
              Cargando datos. Esto podría tardar unos segundos.
            </p>
          </div>
        </div>
      )}
      {!isLoading && (!data || data.length === 0) && (
        <span className="transaction-chart-no-data">
          No se pudo obtener la potencia de carga ni el SoC para mostrar el gráfico.
        </span>
      )}
      {!isLoading && data?.length && (
        <ResponsiveContainer width="100%" height="80%">
          <AreaChart data={data}>
            <XAxis
              dataKey="Hora"
              tickFormatter={(time) => moment.utc(time).tz(timezone).format('hh:mm:ss A')}
              stroke="#fff"
              fontSize={12}
              minTickGap={28}
              interval="preserveStartEnd"
            >
              <Label value="Hora" offset={-5} position="insideBottom" className="chart__label" />
            </XAxis>
            <YAxis
              yAxisId="left"
              domain={[
                0,
                maxValues.maxPotencia === 0 ? 1 : Math.ceil(maxValues.maxPotencia / 10) * 10,
              ]}
              tickCount={5}
              stroke="#fff"
              fontSize={12}
            >
              <Label value="Potencia" angle={-90} position="insideLeft" className="chart__label" />
            </YAxis>
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              tickCount={5}
              stroke="#fff"
              fontSize={12}
              hide={!socValues || socValues.length < 1}
            >
              <Label value="SoC" angle={90} position="insideRight" className="chart__label" />
            </YAxis>
            {data.length > 1 ? (
              <Tooltip
                content={
                  <CustomTooltip
                    payload={data.map((d) => ({
                      value: d.Hora,
                      payload: { SoC: d.SoC || '0', Potencia: d.Potencia || '0' },
                    }))}
                    label="Potencia y SoC"
                    active
                  />
                }
              />
            ) : null}
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="Potencia"
              stroke="#71A0F9"
              fill="#71A0F9"
              dot={{ r: 2 }}
              activeDot={{ r: 3, stroke: '#71A0F9', fill: '#71A0F9' }}
              fillOpacity={0.1}
            />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="SoC"
              stroke="#00FF7F"
              fill="#00FF7F"
              dot={{ r: 2 }}
              activeDot={{ r: 3, stroke: '#00FF7F', fill: '#00FF7F' }}
              fillOpacity={0.1}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default memo(TransactionChart, shouldNotUpdate);
